// ==========================================
// MOTOR DE FECHAMENTO — COMERCIAL INTERNO (política v1, set/2026)
// Espelha a estrutura de comissaoExternaV2.ts (mesmo padrão: função pura de
// resolução por negociação + coleta de SLA + fechamento mensal que junta tudo),
// mas é um motor SEPARADO — o Comercial Interno não usa ativação R$/kWh por
// faixa de atingimento nem rede de parceiros, usa % de tabela por desconto
// (ver src/lib/comissaoInterna.ts).
// ==========================================
import { supabase } from './supabase.ts';
import {
  NegociacaoInternaMes,
  calcularComissoesInterno,
  INTERNO_SLA_LIMIAR_MIN,
  INTERNO_SLA_BONUS_RS,
} from './comissaoInterna.ts';
import type { DetalheNegociacaoInterna } from './comissaoInterna.ts';
import type { RepassePosVenda } from './comissaoIndicacaoPosVenda.ts';
import { computeSlaAgregado } from './slaRanking.ts';
import type { HorarioTrabalho, AusenciaTrabalho, NegociacaoAtribuicao } from '../../types';

const round2 = (v: number) => Math.round(v * 100) / 100;

// O tipo mora em comissaoInterna.ts junto da conta (função pura); reexportado aqui.
export type { DetalheNegociacaoInterna };

/**
 * SLA médio do mês de UM vendedor do Comercial Interno — mesma agregação do
 * bloco de Analytics Comercial (computeSlaAgregado, ver src/lib/slaRanking.ts),
 * só que escopada por competência (mês/ano do fechamento) em vez de um período
 * livre escolhido na tela.
 *
 * ⚠️ Se a regra de "primeira atividade" ou o cálculo de tempo útil mudar,
 * SlaAtendimentoBlock.tsx e esta função mudam juntos — consomem a mesma
 * computeSlaAgregado, então na prática só uma mudou: a origem do dado.
 */
export async function coletarSlaMedioInterno(
  vendedorId: string,
  mes: number,
  ano: number
): Promise<{ mediaUtilMs: number | null; leadsConcluidos: number }> {
  const mesStr = String(mes).padStart(2, '0');
  const lastDay = new Date(ano, mes, 0).getDate();
  const inicio = `${ano}-${mesStr}-01T00:00:00`;
  const fim = `${ano}-${mesStr}-${lastDay}T23:59:59`;

  const { data: atribuicoes } = await supabase
    .from('negociacao_atribuicoes')
    .select('*')
    .eq('responsavel_id', vendedorId)
    .gte('atribuido_em', inicio)
    .lte('atribuido_em', fim);

  const todas = (atribuicoes || []) as NegociacaoAtribuicao[];
  const concluidas = todas.filter(a => a.primeira_atividade_em);
  if (concluidas.length === 0) return { mediaUtilMs: null, leadsConcluidos: 0 };

  const [{ data: horarios }, { data: ausencias }] = await Promise.all([
    supabase.from('horarios_trabalho').select('*').eq('profile_id', vendedorId),
    supabase.from('ausencias_trabalho').select('*').eq('profile_id', vendedorId),
  ]);

  const horariosPorPessoa = new Map<string, HorarioTrabalho[]>([[vendedorId, (horarios || []) as HorarioTrabalho[]]]);
  const ausenciasPorPessoa = new Map<string, AusenciaTrabalho[]>([[vendedorId, (ausencias || []) as AusenciaTrabalho[]]]);

  const agregado = computeSlaAgregado(concluidas, horariosPorPessoa, ausenciasPorPessoa, new Map());
  const consideradas = concluidas.filter(a => !a.sla_desconsiderado);
  return { mediaUtilMs: agregado.mediaUtilGeral, leadsConcluidos: consideradas.length };
}

export interface FechamentoInternoV1 {
  comissaoData: Record<string, any>;
  detalhes: DetalheNegociacaoInterna[];
  /** Vendas indicadas pelo pós-venda: a parte que vai para ele (linha própria na Folha, #6236). */
  repassesPosVenda: RepassePosVenda[];
}

/**
 * Fechamento mensal completo do Comercial Interno para um vendedor.
 *
 * `is_grupo_a` vem PRÉ-COMPUTADO por quem chama (VendedoresTab.tsx já roda
 * isGrupoTarifarioA sobre todas as negociações do mês, de qualquer canal, antes
 * de despachar por vendedor) — mesma convenção de calcularFechamentoExternoV2,
 * que também recebe o campo pronto em vez de recalcular. Grupo A fica fora do
 * cálculo automático: são condições comerciais que exigem negociação manual do
 * gerente independente de quem vendeu.
 */
export async function calcularFechamentoInternoV1(params: {
  vendedorId: string;
  mes: number;
  ano: number;
  negs: Array<NegociacaoInternaMes & { is_grupo_a: boolean }>;
  salarioBase: number;
  metaKwh: number;
  tarifaValor: number;
  tarifaId: number | null;
}): Promise<FechamentoInternoV1> {
  const { vendedorId, mes, ano, negs, salarioBase, tarifaValor, tarifaId } = params;

  // Comissão de cada negociação — mesma função que a barra "Meu Trabalho" do CRM usa.
  // Sem meta configurada (ainda) → 0% de atingimento (pior faixa do pilar Volume),
  // nunca herda a meta do vendedor externo (populações diferentes).
  const comissoes = calcularComissoesInterno(negs, params.metaKwh, tarifaValor);
  const { volumeTotal, pctAtingimentoMeta, detalhes, ignoradas, valorComissaoTotal } = comissoes;
  const memoriaNegociacoes = comissoes.memoria;

  // Bônus de SLA: fixo, pago 1x no fechamento (não por negociação) — média
  // mensal do SLA útil dela, decisão do dono do produto (09/09/2026).
  const sla = await coletarSlaMedioInterno(vendedorId, mes, ano);
  const slaCumprido = sla.mediaUtilMs !== null && sla.mediaUtilMs <= INTERNO_SLA_LIMIAR_MIN * 60_000;
  const valorBonusSla = slaCumprido ? INTERNO_SLA_BONUS_RS : 0;

  const valorTotal = round2(salarioBase + valorComissaoTotal + valorBonusSla);

  return {
    comissaoData: {
      vendedor_id: vendedorId,
      mes,
      ano,
      politica: 'interno_v1',
      total_kwh_vendido: volumeTotal,
      meta_kwh: params.metaKwh,
      percentual_atingimento: pctAtingimentoMeta,
      // Não existe UM percentual de comissão pra competência inteira (cada
      // negociação tem o seu, na tabela) — mesma convenção que a política v2
      // do vendedor externo já usa (percentual_comissao: 0, detalhe de verdade
      // em outro lugar).
      percentual_comissao: 0,
      tarifa_energia_valor: tarifaValor,
      tarifa_energia_id: tarifaId,
      valor_comissao_kwh: round2(valorComissaoTotal),
      valor_bonus: valorBonusSla,
      salario_base: salarioBase,
      valor_total: valorTotal,
      // sla_detalhe fica null de propósito: a coluna é tipada pro shape do SLA
      // de esforço do vendedor externo (visitas/posVenda/fechamentos/parceiros),
      // que não tem nada a ver com este SLA de atendimento. Os números de
      // verdade vão em detalhe_calculo.sla_interno, que é genérico.
      sla_cumprido: sla.mediaUtilMs !== null ? slaCumprido : null,
      detalhe_calculo: {
        sla_interno: {
          media_util_ms: sla.mediaUtilMs,
          leads_concluidos: sla.leadsConcluidos,
          limiar_min: INTERNO_SLA_LIMIAR_MIN,
          bonus_rs: valorBonusSla,
        },
        negociacoes: memoriaNegociacoes,
        negociacoes_ignoradas: ignoradas,
        negociacoes_grupo_a: comissoes.grupoA,
        indicacoes_pos_venda: comissoes.repassesPosVenda,
      },
    },
    detalhes,
    repassesPosVenda: comissoes.repassesPosVenda,
  };
}
